import React from 'react';

const CONTENT_BASE = 'https://content.olympiq.co.uk/images/';

const SCREENSHOTS = [
  '120_1x_shots_so.png',
  '578_1x_shots_so.png',
  '243_1x_shots_so.png',
  '970_1x_shots_so.png',
  '372_1x_shots_so.png',
  '792_1x_shots_so.png',
  '872_1x_shots_so.png',
];

const ScreenshotCarousel = (): React.JSX.Element => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const updateArrows = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const getCardScrollAmount = React.useCallback((): number => {
    const container = scrollRef.current;
    if (!container) return 200;
    const firstCard = container.querySelector('[data-card="true"]') as HTMLElement | null;
    const style = window.getComputedStyle(container);
    const gap =
      parseFloat(style.columnGap || style.gap || '0') ||
      parseFloat(style.getPropertyValue('gap') || '0') ||
      0;
    const width = firstCard?.getBoundingClientRect().width ?? 200;
    return width + gap;
  }, []);

  const scrollByOne = React.useCallback(
    (direction: 'left' | 'right') => {
      const el = scrollRef.current;
      if (!el) return;
      const amount = getCardScrollAmount();
      el.scrollBy({ left: direction === 'right' ? amount : -amount, behavior: 'smooth' });
    },
    [getCardScrollAmount],
  );

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollByOne('right');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollByOne('left');
      }
    },
    [scrollByOne],
  );

  return (
    <div className="relative">
      {/* Scroll track */}
      <div
        ref={scrollRef}
        role="region"
        aria-label="App screenshot carousel"
        onMouseEnter={() => window.addEventListener('keydown', handleKeyDown)}
        onMouseLeave={() => window.removeEventListener('keydown', handleKeyDown)}
        onFocus={() => window.addEventListener('keydown', handleKeyDown)}
        onBlur={() => window.removeEventListener('keydown', handleKeyDown)}
        className="flex snap-x snap-mandatory overflow-x-auto no-scrollbar gap-4 pb-4"
        style={{ scrollBehavior: 'smooth' }}
      >
        {SCREENSHOTS.map((filename, idx) => (
          <div
            key={filename}
            data-card="true"
            className="snap-start shrink-0 w-[180px] sm:w-[200px] md:w-[220px] rounded-2xl overflow-hidden"
          >
            <img
              src={`${CONTENT_BASE}${filename}`}
              alt={`App screenshot ${idx + 1}`}
              className="w-full aspect-[9/16] object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Left arrow */}
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => scrollByOne('left')}
        disabled={!canScrollLeft}
        className={`absolute left-0 top-1/2 -translate-y-1/2 -ml-3 rounded-full p-2 shadow-lg backdrop-blur bg-white/80 dark:bg-neutral-800/80 ring-1 ring-black/5 dark:ring-white/10 transition-opacity ${
          canScrollLeft ? 'opacity-100' : 'opacity-40 cursor-not-allowed'
        }`}
      >
        <span className="sr-only">Previous</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          className="fill-none stroke-current text-neutral-700 dark:text-neutral-200"
        >
          <path d="M15 18l-6-6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Right arrow */}
      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => scrollByOne('right')}
        disabled={!canScrollRight}
        className={`absolute right-0 top-1/2 -translate-y-1/2 -mr-3 rounded-full p-2 shadow-lg backdrop-blur bg-white/80 dark:bg-neutral-800/80 ring-1 ring-black/5 dark:ring-white/10 transition-opacity ${
          canScrollRight ? 'opacity-100' : 'opacity-40 cursor-not-allowed'
        }`}
      >
        <span className="sr-only">Next</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          className="fill-none stroke-current text-neutral-700 dark:text-neutral-200"
        >
          <path d="M9 18l6-6-6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
};

export default ScreenshotCarousel;
